import { PureComponent } from 'react';
import { parse } from 'query-string';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from '@reach/router';
import { connect, MapStateToProps } from 'react-redux';
import {
  appDispatch,
  IAppInstance,
  IProviders,
  ITasks,
  IUsers,
  RootState,
} from 'store/store';
import { searchDocuments } from 'store/providers/providerSlice';
import { DataContextProvider } from 'store/context/DataContext';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import DocumentManager from '../Panels/DocumentManager/DocumentManager';
import LogoLoading from '../../../components/LogoLoading/LogoLoading';
import { ICompany, TProjects } from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ItemSearchField } from 'store/providers/providerTypes';
import { getCompanyHandler } from 'store/companies/companiesSlice';

const JSDocumentManager: any = DocumentManager;

interface IStateToProps {
  tasks: ITasks;
  users: IUsers;
  company?: ICompany;
  provider: IProviders['map'][0];
  thisUser: IThisUserState;
  projects: TProjects;
  appInstance: IAppInstance;
  providerItems: IProviders['items'][0];
  searchInFolderLoading: boolean;
}

interface IOwnProps {
  providerId?: string;
  folderId?: string;
}

type IComponentProps = IOwnProps & IStateToProps & RouteComponentProps;

interface IContentState {
  loading: boolean;
}

class ContentSearch extends PureComponent<IComponentProps, IContentState> {
  state = {
    loading: true,
  };

  componentDidMount() {
    if (this.props.location) {
      this.searchDocuments(this.props.location.search);
    }

    const companyId = this.props.provider.data!.managingCompanyId;
    if (companyId) {
      appDispatch(
        getCompanyHandler({
          companyId,
          templateStub: null,
          companyStub: false,
          projectStub: false,
        })
      )
        .then(() => {
          this.setState({ loading: false });
        })
        .catch((err: any) => {
          display(err);
          this.setState({ loading: false });
        });
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps: Readonly<IComponentProps>) {
    if (this.props.location && this.props.location !== prevProps.location) {
      this.searchDocuments(this.props.location.search);
    }
  }

  searchDocuments = (search: string) => {
    const providerId = Number(this.props.providerId);
    const baseFolderId = this.props.folderId
      ? Number(this.props.folderId)
      : undefined;

    const parsedQuery = parse(search);
    if (parsedQuery) {
      const query = parsedQuery.q as string;
      const folderId = parsedQuery.folder
        ? Number(parsedQuery.folder)
        : undefined;
      const searchFields =
        typeof parsedQuery.fields === 'string'
          ? parsedQuery.fields.split(',')
          : ['name'];
      appDispatch(
        searchDocuments({
          searchTerm: search,
          query,
          providerId,
          searchFields: searchFields as ItemSearchField[],
          start: 0,
          count: 100,
          folderId: folderId || baseFolderId,
        })
      );
    }
  };

  render() {
    const {
      users,
      tasks,
      company,
      projects,
      provider,
      thisUser,
      appInstance,
      providerItems,
      searchInFolderLoading,
      folderId,
    } = this.props;
    if (!provider || provider.isLoading) {
      return <LogoLoading />;
    }
    let searchTerm;
    if (this.props.location) {
      searchTerm = this.props.location.search;
    }
    return (
      <div style={{ height: '100%' }}>
        <Helmet>
          <title>{messages.contentManagement}</title>
          <meta name="description" content={messages.contentManagement} />
        </Helmet>
        <div className="row" style={{ height: '100%' }}>
          <div id="dm" style={{ height: '100%' }}>
            <DataContextProvider
              value={{ providerId: Number(this.props.providerId) }}
            >
              <JSDocumentManager
                tasks={tasks}
                users={users}
                userId={Number(thisUser.userId)}
                company={company}
                dispatch={appDispatch}
                projects={projects}
                provider={provider.data}
                searchTerm={searchTerm}
                providerItems={providerItems}
                isUserManager={appInstance.managerId === provider.data?.id}
                searchInFolderLoading={
                  searchInFolderLoading || this.state.loading
                }
                rootFolderId={folderId ? Number(folderId) : undefined}
              />
            </DataContextProvider>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const thisUser = state.thisUser;
  const users = state.users;
  const appInstance = state.appInstance;
  const providerId = Number(props.providerId);
  const provider = state.providers.map[providerId];
  const providerItems = state.providers.items[providerId];
  const searchInFolderLoading = state.app.searchInFolder.loading;
  const companyId = provider.data?.managingCompanyId || null;
  const company = state.companies.data[companyId!];
  const projects = company?.projects;
  const tasks = state.tasks;

  return {
    provider,
    thisUser,
    providerItems,
    users,
    appInstance,
    company,
    projects,
    tasks,
    searchInFolderLoading,
  };
};

export default connect(mapStateToProps)(ContentSearch);
